import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BeoIcon } from "@components/common/view";
import { Dialog } from "primereact/dialog";
import { useCallback, useEffect, useRef, useState } from "react"; // Explicitly import React for FC type if needed
/**
 * The inactivity timeout duration in minutes. Defaults to 20 minutes.
 * @type {number}
 */
var INACTIVITY_TIMEOUT_MINUTES = Number(window._env_.INACTIVITY_TIMEOUT_MINUTES) || 20;
/**
 * The inactivity limit in milliseconds.
 * @type {number}
 */
var INACTIVITY_LIMIT = INACTIVITY_TIMEOUT_MINUTES * 60 * 1000; // Convert minutes to ms
/**
 * The countdown duration in seconds before automatic logout.
 * @type {number}
 */
var LOGOUT_COUNTDOWN = 30; // in seconds
/**
 * InactivityHandler component monitors user activity and displays a warning dialog
 * before automatically logging out the user due to inactivity.
 * It provides a countdown and an option to reset the timer.
 *
 * @component
 * @param {InactivityHandlerProps} props - The component's properties.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
var InactivityHandler = function (_a) {
    var onLogout = _a.onLogout;
    /**
     * State to control the visibility of the inactivity warning dialog.
     * @type {[boolean, function]}
     */
    var _b = useState(false), visible = _b[0], setVisible = _b[1];
    /**
     * State for the countdown timer displayed in the dialog.
     * @type {[number, function]}
     */
    var _c = useState(LOGOUT_COUNTDOWN), countdown = _c[0], setCountdown = _c[1];
    /**
     * Ref for the main inactivity timer.
     * @type {React.MutableRefObject<NodeJS.Timeout | null>}
     */
    var inactivityTimer = useRef(null);
    /**
     * Ref for the logout timer, which triggers the `onLogout` callback.
     * @type {React.MutableRefObject<NodeJS.Timeout | null>}
     */
    var logoutTimer = useRef(null);
    /**
     * Ref for the interval that updates the countdown display.
     * @type {React.MutableRefObject<NodeJS.Timeout | null>}
     */
    var countdownInterval = useRef(null);
    /**
     * Resets all inactivity and logout timers, hides the dialog,
     * and restarts the main inactivity timer.
     * This function is memoized with `useCallback` to prevent unnecessary re-creations.
     */
    var resetInactivityTimer = useCallback(function () {
        // Clear existing timers to prevent multiple timers running simultaneously
        if (inactivityTimer.current)
            clearTimeout(inactivityTimer.current);
        if (logoutTimer.current)
            clearTimeout(logoutTimer.current);
        if (countdownInterval.current)
            clearInterval(countdownInterval.current);
        // Reset component states
        setVisible(false);
        setCountdown(LOGOUT_COUNTDOWN);
        // Start the main inactivity timer
        // If no activity occurs within INACTIVITY_LIMIT, show the dialog and start logout countdown
        inactivityTimer.current = setTimeout(function () {
            setVisible(true); // Show the inactivity warning dialog
            setCountdown(LOGOUT_COUNTDOWN); // Reset countdown for the dialog
            // Start the actual logout timer
            logoutTimer.current = setTimeout(function () {
                clearInterval(countdownInterval.current); // Stop countdown interval
                onLogout(); // Trigger the logout function
            }, LOGOUT_COUNTDOWN * 1000); // Logout after countdown duration
            // Start the countdown display interval
            countdownInterval.current = setInterval(function () {
                setCountdown(function (prev) {
                    if (prev <= 1) {
                        clearInterval(countdownInterval.current); // Stop interval when countdown reaches 0
                        return 0;
                    }
                    return prev - 1; // Decrement countdown
                });
            }, 1000); // Update every second
        }, INACTIVITY_LIMIT); // Main inactivity timer duration
    }, [onLogout]); // Dependency: onLogout callback
    /**
     * Effect hook to set up and clean up event listeners for user activity.
     * Listens for mouse movements, key presses, scrolling, and clicks to reset the inactivity timer.
     * Clears all timers on component unmount.
     */
    useEffect(function () {
        // List of events to monitor for user activity
        var events = ["mousemove", "keydown", "scroll", "click"];
        var resetHandler = function () { return resetInactivityTimer(); };
        // Add event listeners
        events.forEach(function (event) { return window.addEventListener(event, resetHandler); });
        resetInactivityTimer(); // Initialize the timer on component mount
        // Cleanup function: remove event listeners and clear all timers
        return function () {
            events.forEach(function (event) {
                return window.removeEventListener(event, resetHandler);
            });
            if (inactivityTimer.current)
                clearTimeout(inactivityTimer.current);
            if (logoutTimer.current)
                clearTimeout(logoutTimer.current);
            if (countdownInterval.current)
                clearInterval(countdownInterval.current);
        };
    }, [resetInactivityTimer]); // Dependency: resetInactivityTimer callback
    /**
     * Template function for the header of the inactivity warning dialog.
     * @returns {JSX.Element} The JSX for the dialog header.
     */
    var headerTemplate = function () {
        return (_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(BeoIcon, { id: "warning-icon", className: "pi pi-exclamation-triangle text-yellow-500 text-2xl" }), _jsx("span", { className: "text-yellow-600 text-xl font-semibold", children: "Session Expiring Soon" })] }));
    };
    return (_jsx(Dialog, { onHide: function () { }, header: headerTemplate, visible: visible, style: { width: "35vw" }, closable: false, modal // Makes the dialog a modal (blocks interaction with underlying content)
        : true, className: "rounded-2xl shadow-xl" // Styling for the dialog container
        , contentClassName: "pb-6" // Padding for dialog content
        , footer: _jsx("div", { className: "flex justify-end px-6", children: _jsx("button", { className: "bg-green-600 hover:bg-green-700  font-medium py-2 px-6 rounded transition duration-200 text-white" // Added text-white for visibility
                , onClick: resetInactivityTimer, children: "I'm still here" }) }), children: _jsxs("div", { className: "text-center px-6", children: [_jsxs("p", { className: "text-base mb-4 text-gray-700 dark:text-gray-300", children: [" ", "You've been inactive for a while. For your security, you\u2019ll be logged out automatically in:"] }), _jsxs("div", { className: "text-4xl font-bold text-red-600 mb-4", children: [countdown, "s"] }), " ", _jsx("p", { className: "text-sm text-gray-500", children: "Click the button below if you're still around." })] }) }));
};
export default InactivityHandler;
